import { useEffect } from 'react';
import { siteInfo } from '@/mocks/site';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  isArticle?: boolean;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
}

export default function SEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  jsonLd,
  isArticle = false,
  articlePublishedTime,
  articleModifiedTime,
}: SEOProps) {
  const fullTitle = `${title} | ${siteInfo.fullName}`;
  const canonical = canonicalUrl || siteInfo.url;

  useEffect(() => {
    document.title = fullTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);

    setMeta('og:title', fullTitle, true);
    setMeta('og:description', description, true);
    setMeta('og:type', isArticle ? 'article' : ogType, true);
    setMeta('og:url', canonical, true);
    if (ogImage) setMeta('og:image', ogImage, true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);
    if (ogImage) setMeta('twitter:image', ogImage);

    const canonicalEl = document.querySelector('link[rel="canonical"]');
    if (canonicalEl) canonicalEl.setAttribute('href', canonical);

    if (isArticle && articlePublishedTime) {
      setMeta('article:published_time', articlePublishedTime, true);
      if (articleModifiedTime) setMeta('article:modified_time', articleModifiedTime, true);
    }
  }, [fullTitle, description, keywords, canonical, ogType, ogImage, isArticle, articlePublishedTime, articleModifiedTime]);

  useEffect(() => {
    if (!jsonLd) return;

    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach((s) => s.remove());

    const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    items.forEach((item) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(item);
      document.head.appendChild(script);
    });

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach((s) => s.remove());
    };
  }, [jsonLd]);

  return null;
}